// src/pages/Login.js
// ==================== Imports ====================
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import logo from '../Images/swapiify.png';
import { apiFetch } from '../utils/api';


// ==================== Login Component ====================
export default function Login() {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      // Use the central apiFetch wrapper (reads your config)
      const result = await apiFetch('/login', {
        method: 'POST',
        body: JSON.stringify({
          email: formData.username, // your Laravel expects email
          password: formData.password
        })
      });

      // If your API returns user inside result.user or result.data, handle both
      const userObject = result?.user || result?.data || result;

      // success - keep message and save user
      setMessage('✅ Login successful!');

      try {
        localStorage.setItem('user', JSON.stringify(userObject));
      } catch (storageErr) {
        // ignore storage errors but log for debugging
        console.warn('Could not save user to localStorage', storageErr);
      }

      if (formData.rememberMe) {
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('rememberMe');
      }

      // Notify other parts of app that storage changed
      window.dispatchEvent(new Event('storageChange'));

      // navigate to dashboard (short delay for UX)
      setTimeout(() => navigate('/dashboard'), 300);
    } catch (err) {
      console.error('Login error:', err);
      // Try to show a helpful message coming from the API wrapper
      const errMsg =
        err?.body?.message ||
        err?.body?.error ||
        err?.message ||
        '❌ Error connecting to server';
      setMessage(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header Section */}
        <div style={styles.header}>
          <div style={styles.logo}>
            <img src={logo} alt="Swapiify Logo" style={styles.logoImage} />
          </div>

          <h2 style={styles.heading}>Welcome Back !</h2>
          <p style={styles.subtitle}>Sign in to continue to Swapiify.</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} style={styles.form}>
          {/* Username Input */}
          <div style={styles.inputGroup}>
            <label htmlFor="username" style={styles.label}>
              Username
            </label>
            <input
              type="text"
              id="username"
              name="username"
              placeholder="Enter username"
              value={formData.username}
              onChange={handleChange}
              required
              style={styles.input}
              disabled={loading}
            />
          </div>

          {/* Password Input */}
          <div style={styles.inputGroup}>
            <label htmlFor="password" style={styles.label}>
              Password
            </label>
            <div style={styles.passwordContainer}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                placeholder="Enter password"
                value={formData.password}
                onChange={handleChange}
                required
                style={styles.passwordInput}
                disabled={loading}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                style={styles.passwordToggle}
                disabled={loading}
              >
                {showPassword ? '👁️' : '🙈'}
              </button>
            </div>
          </div>

          {/* Remember Me & Forgot Password */}
          <div style={styles.optionsContainer}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleChange}
                style={styles.checkbox}
                disabled={loading}
              />
              Remember me
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {})
            }}
          >
            {loading ? (
              <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                Logging in...
              </div>
            ) : (
              'Sign In'
            )}
          </button>

          {/* Message Display */}
          {message && (
            <div
              style={{
                ...styles.message,
                ...(message.includes('✅') ? styles.messageSuccess : styles.messageError)
              }}
            >
              {message}
            </div>
          )}
        </form>

        {/* Footer */}
        <div style={styles.footer}>
          <p style={styles.footerText}>
            Don't have an account?{' '}
            <Link to="/register" style={styles.link}>
              Signup
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

// ==================== Exact Styles from Image ====================
const styles = {
  container: {
    minHeight: '80vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8f9fa',
    padding: '20px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
  },
  card: {
    width: '100%',
    maxWidth: '460px',
    background: 'white',
    borderRadius: '7px',
    padding: '48px 40px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e5e7eb'
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px'
  },

  logoImage: {
    width: '200px',
  },

  logo: {
    marginBottom: '24px'
  },
  logoText: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#405189',
    letterSpacing: '0.5px'
  },
  heading: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#495057',
    margin: '0 0 8px 0',
    lineHeight: '1.4'
  },
  subtitle: {
    fontSize: '14px',
    color: '#878a99',
    margin: '0',
    lineHeight: '1.5'
  },
  form: {
    marginBottom: '24px'
  },
  inputGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#495057'
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '14px',
    transition: 'all 0.15s ease-in-out',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    ':focus': {
      outline: 'none',
      borderColor: '#405189',
      boxShadow: '0 0 0 2px rgba(64, 81, 137, 0.1)'
    },
    ':disabled': {
      backgroundColor: '#f8f9fa',
      cursor: 'not-allowed'
    }
  },
  passwordContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  passwordInput: {
    width: '100%',
    padding: '12px 45px 12px 16px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '14px',
    transition: 'all 0.15s ease-in-out',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    ':focus': {
      outline: 'none',
      borderColor: '#405189',
      boxShadow: '0 0 0 2px rgba(64, 81, 137, 0.1)'
    },
    ':disabled': {
      backgroundColor: '#f8f9fa',
      cursor: 'not-allowed'
    }
  },
  passwordToggle: {
    position: 'absolute',
    right: '12px',
    background: 'none',
    border: 'none',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    color: '#6c757d',
    ':hover': {
      backgroundColor: '#f8f9fa'
    },
    ':disabled': {
      cursor: 'not-allowed',
      opacity: '0.5'
    }
  },
  optionsContainer: {
    marginBottom: '24px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    color: '#495057',
    cursor: 'pointer',
    fontWeight: '400'
  },
  checkbox: {
    marginRight: '8px',
    width: '16px',
    height: '16px',
    accentColor: '#405189'
  },
  button: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: '#FF7F50',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s ease-in-out',
    ':hover': {
      backgroundColor: '#38467a'
    }
  },
  buttonDisabled: {
    backgroundColor: '#9fa8c7',
    cursor: 'not-allowed',
    ':hover': {
      backgroundColor: '#9fa8c7'
    }
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid transparent',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  message: {
    padding: '12px 16px',
    borderRadius: '6px',
    marginTop: '16px',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: '400'
  },
  messageSuccess: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
    border: '1px solid #a7f3d0'
  },
  messageError: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: '1px solid #fca5a5'
  },
  socialSection: {
    marginBottom: '24px'
  },
  divider: {
    position: 'relative',
    textAlign: 'center',
    margin: '24px 0',
    '::before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '0',
      right: '0',
      height: '1px',
      backgroundColor: '#e5e7eb'
    }
  },
  dividerText: {
    display: 'inline-block',
    padding: '0 12px',
    backgroundColor: 'white',
    color: '#878a99',
    fontSize: '13px',
    fontWeight: '400',
    position: 'relative',
    zIndex: 1
  },
  socialPlaceholder: {
    textAlign: 'center',
    padding: '16px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#6c757d',
    border: '1px dashed #dee2e6'
  },
  footer: {
    textAlign: 'center',
    paddingTop: '16px',
    borderTop: '1px solid #e5e7eb'
  },
  footerText: {
    fontSize: '14px',
    color: '#495057',
    margin: '0'
  },
  link: {
    color: '#405189',
    textDecoration: 'none',
    fontWeight: '500',
    ':hover': {
      textDecoration: 'underline'
    }
  }
};

// Add CSS animation for spinner (only once)
if (typeof document !== 'undefined' && !document.getElementById('swapiify-spinner-style')) {
  const spinnerStyle = document.createElement('style');
  spinnerStyle.id = 'swapiify-spinner-style';
  spinnerStyle.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(spinnerStyle);
}
